import resolveLanguageKey from '../../../utils/resolveLanguageKey';
import { supported as supportedLanguages } from '../../../translations/i18n';

const DEFAULT_LOCALE = 'es-MX';
const DEFAULT_CURRENCY = 'MXN';
const LANGUAGE_TAG = /^[a-z]{2}-[A-Z]{2}$/;
const CURRENCY_CODE = /^[A-Z]{3}$/;

const priceFormatter = new Intl.NumberFormat(DEFAULT_LOCALE, {
  style: 'currency',
  currency: DEFAULT_CURRENCY,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const priceFormatterCompact = new Intl.NumberFormat(DEFAULT_LOCALE, {
  style: 'currency',
  currency: DEFAULT_CURRENCY,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const formatPrice = price => priceFormatter.format(price);

export const formatPriceCompact = price => priceFormatterCompact.format(price);

// Shared across widgets on the same page: the key carries both inputs.
const compactFormatters = new Map();

const getCompactFormatter = (locale, currency) => {
  const key = `${locale}|${currency}`;
  if (!compactFormatters.has(key)) {
    compactFormatters.set(
      key,
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    );
  }
  return compactFormatters.get(key);
};

/**
 * Normalizes a `data-prop-currency` value: trimmed and upper-cased, `''` when absent.
 */
export const normalizeCurrency = raw => (typeof raw === 'string' ? raw.trim().toUpperCase() : '');

let knownCurrencies;

/**
 * A three-letter code the browser knows as a currency. `Intl.supportedValuesOf` is in
 * every browser the build targets and rejects well-formed codes that are not
 * currencies (`XYZ`); constructing a formatter is the fallback, and it only rejects
 * malformed ones.
 */
export const isValidCurrency = code => {
  if (!CURRENCY_CODE.test(code)) return false;
  if (knownCurrencies === undefined) {
    knownCurrencies =
      typeof Intl.supportedValuesOf === 'function' ? new Set(Intl.supportedValuesOf('currency')) : null;
  }
  if (knownCurrencies) return knownCurrencies.has(code);
  try {
    getCompactFormatter(DEFAULT_LOCALE, code);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Number locale for a widget language. An unsupported language falls back to `es-MX`,
 * as the translations do. BCP 47 tags are kept as they are; the word aliases
 * (`spanish`, `english`, `chinese`) go through `resolveLanguageKey`. The tags do not,
 * because it maps `es-CO` to `es-MX` for translations and `es-MX` renders COP as
 * `COP 1,875` instead of `$ 1.875`.
 */
const toNumberLocale = lang => {
  if (!supportedLanguages.includes(lang)) return DEFAULT_LOCALE;
  return LANGUAGE_TAG.test(lang) ? lang : resolveLanguageKey(lang);
};

/**
 * Compact (no decimals) price formatter for the availability calendar.
 *
 * Without a valid `currency` it is `formatPriceCompact`: Mexican pesos with the
 * `es-MX` locale whatever the language, so embeds that do not declare a currency
 * render exactly what they did before `data-prop-currency` existed. With one, it
 * formats in that currency using the widget language as locale (`pt-BR` + `BRL`
 * renders `R$ 175`). An unsupported language, or a locale `Intl` rejects, falls back
 * to `es-MX`.
 */
export const createCompactPriceFormatter = options => {
  const { currency, locale } = options || {};
  const code = normalizeCurrency(currency);
  if (!code || !isValidCurrency(code)) return formatPriceCompact;

  let formatter;
  try {
    formatter = getCompactFormatter(toNumberLocale(locale), code);
  } catch (e) {
    formatter = getCompactFormatter(DEFAULT_LOCALE, code);
  }
  return price => formatter.format(price);
};

export const PRICE_LEVEL_CLASS = {
  cheap: 'udp-day--cheap',
  normal: 'udp-day--normal',
  expensive: 'udp-day--expensive',
};
